// Add or edit a client. Invoices keep a copy of the client's details as
// they were, so editing here never changes invoices already made.
import { type FormEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { type Client, isEmailAddress } from '@/core/client';
import { nowStamp } from '@/core/dates';
import { newId } from '@/core/ids';
import { track } from '@/data/analytics';
import { deleteClient, saveClient } from '@/data/store';
import { t } from '@/l10n/strings';
import { Button, EmptyState, PageHead, PageLoading, TextArea, TextField } from '@/ui/controls';
import { ConfirmDialog } from '@/ui/Dialog';
import { useToast } from '@/ui/Toasts';

export function ClientFormPage() {
  const { id } = useParams();
  const { clients } = useAppData();
  if (!clients) return <PageLoading />;
  const existing = id ? clients.find((c) => c.id === id) : undefined;
  if (id && !existing) return <EmptyState badge="users" title={t('clientFormThisClientIsGone')} body={t('clientFormTheyMayHaveBeenDeleted')} />;
  return <ClientForm key={id ?? 'new'} existing={existing} />;
}

function ClientForm({ existing }: { existing?: Client }) {
  const { user, business } = useAppData();
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    company: existing?.company ?? '',
    email: existing?.email ?? '',
    phone: existing?.phone ?? '',
    address: existing?.address ?? '',
    notes: existing?.notes ?? '',
  });
  const [showErrors, setShowErrors] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  usePageTitle(existing ? t('clientFormEditClient') : t('clientFormNewClient'));
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const nameError = !form.name.trim() && !form.company.trim() ? t('errorNameOrCompanyMissing') : null;
  const emailError = form.email.trim() && !isEmailAddress(form.email) ? t('errorEmailInvalid') : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (nameError || emailError || !user) return;
    const now = nowStamp();
    const trimmed = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()])) as typeof form;
    const client: Client = existing ? { ...existing, ...trimmed, updatedAt: now } : { id: newId(), ...trimmed, businessId: business?.id ?? null, createdAt: now, updatedAt: now };
    saveClient(user.uid, client);
    if (existing) track('client_updated');
    else track('client_added', { source: 'form', from_contacts: false });
    navigate(`/clients/${client.id}`, { replace: true });
  };

  return (
    <div className="page page--narrow">
      <PageHead back={existing ? { to: `/clients/${existing.id}`, label: existing.company || existing.name } : { to: '/clients', label: t('navClients') }} title={existing ? t('clientFormEditClient') : t('clientFormNewClient')} />
      <form className="card stack-4" onSubmit={submit} noValidate>
        <div className="grid-2">
          <TextField label={t('clientFormName')} value={form.name} onChange={set('name')} error={showErrors ? nameError : null} autoFocus autoComplete="off" />
          <TextField label={t('clientFormCompany')} value={form.company} onChange={set('company')} aside={<span className="caption">{t('clientFormOptional')}</span>} autoComplete="off" />
        </div>
        <div className="grid-2">
          <TextField label={t('clientFormEmail')} type="email" value={form.email} onChange={set('email')} error={showErrors ? emailError : null} autoComplete="off" />
          <TextField label={t('clientFormPhone')} type="tel" value={form.phone} onChange={set('phone')} placeholder={t('clientFormEG08021112233')} autoComplete="off" />
        </div>
        <TextArea label={t('clientFormAddress')} value={form.address} onChange={set('address')} rows={3} />
        <TextArea label={t('clientFormNotes')} value={form.notes} onChange={set('notes')} rows={3} help={t('clientFormOnlyYouSeeThis')} />
        <div className="between">
          {existing ? (
            <Button variant="danger" icon="trash" onClick={() => setConfirmDelete(true)}>
              {t('clientFormDeleteClient')}
            </Button>
          ) : (
            <span />
          )}
          <div className="row">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              {t('commonCancel')}
            </Button>
            <Button type="submit" variant="action">
              {existing ? t('clientFormSaveChanges') : t('clientFormSaveClient')}
            </Button>
          </div>
        </div>
      </form>
      {existing && (
        <ConfirmDialog
          open={confirmDelete}
          danger
          title={t('clientFormDeleteThisClient')}
          message={t('clientFormTheirInvoicesStayInYour')}
          confirmLabel={t('clientFormDeleteClient')}
          onConfirm={() => {
            if (!user) return;
            deleteClient(user.uid, existing.id);
            track('client_deleted');
            toast(t('clientFormClientDeleted'));
            navigate('/clients', { replace: true });
          }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
